import { memo } from "react";
import { Box } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { ITimingSummary } from "../../../interfaces/dashboard";
import {
  formatDaysValue,
  formatNumber,
} from "../../../utils/metricsFormatters";
import { SEVERITY_COLOR } from "../../../utils/MetricThresholds";
import KpiCard from "../KpiCard";

interface ITimingKpisProps {
  data: ITimingSummary;
  // null cuando el resumen (que aporta el total) todavía no cargó o falló: en
  // ese caso no se muestra un "% del total" engañoso (0%), sino "—".
  totalOrders: number | null;
  onClickOutsideSla?: () => void;
  onClickCriticalSla?: () => void;
}

const rateOf = (count: number, total: number | null): number | null =>
  typeof total === "number" && total > 0
    ? Math.round((count / total) * 100)
    : null;

const rateSuffix = (rate: number | null): string =>
  rate === null ? "" : ` · ${rate}% del total`;

const TimingKpis = ({
  data,
  totalOrders,
  onClickOutsideSla,
  onClickCriticalSla,
}: ITimingKpisProps) => {
  const outsideSlaRate = rateOf(data.ordersOutsideInitialSla, totalOrders);
  const criticalSlaRate = rateOf(data.ordersCriticalSla, totalOrders);

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
        gridAutoRows: "134px",
      }}
    >
      {/* Tarjetas de tiempo: valor grande + "días" como unidad chica, sin color
          ni comparativa de período. */}
      <KpiCard
        title="Tiempo promedio despacho"
        value={formatDaysValue(data.avgDispatchDays)}
        unit="días"
      />
      <KpiCard
        title="Tiempo promedio entrega"
        value={formatDaysValue(data.avgDeliveryDays)}
        unit="días"
      />
      {/* Alertas (Figma): fondo suave + ícono + número coloreado + subtítulo
          descriptivo. Fuera de SLA en amarillo, críticos en rojo. */}
      <KpiCard
        title="Pedidos fuera de SLA inicial"
        value={formatNumber(data.ordersOutsideInitialSla)}
        severity="warning"
        colorValue
        icon={
          <WarningAmberIcon
            sx={{ fontSize: 18, color: SEVERITY_COLOR.warning }}
          />
        }
        subtitle={`Demora inicial de envíos sin despacho > 48 hs${rateSuffix(outsideSlaRate)}`}
        onClick={onClickOutsideSla}
      />
      <KpiCard
        title="Pedidos críticos SLA"
        value={formatNumber(data.ordersCriticalSla)}
        severity="error"
        colorValue
        icon={
          <ReportProblemIcon sx={{ fontSize: 18, color: SEVERITY_COLOR.error }} />
        }
        subtitle={`Riesgo de incumplimiento de envíos sin estado final > 7 días${rateSuffix(criticalSlaRate)}`}
        onClick={onClickCriticalSla}
      />
    </Box>
  );
};

export default memo(TimingKpis);
